/**
 * Event Types and Interfaces
 * Phase 2: System Modules Layer - Event System
 * 
 * Defines all event types and their payload interfaces for the FreshCards subsystem.
 */

/**
 * Event handler function type
 */
export type EventHandler = (data: any) => void

/**
 * Event categories for organization
 */
export const EventCategories = {
  BLOCK: 'block',
  PAGE: 'page',
  COMMAND: 'command',
  EDITOR: 'editor',
  DATA: 'data',
  MODULE: 'module',
  SURFACE: 'surface',
  NOTION: 'notion',
  NAVIGATION: 'navigation',
  SYSTEM: 'system'
} as const

/**
 * Block Events
 */
export interface BlockEventData {
  block: any
  blockId?: string
  type?: string
  afterBlockId?: string
  updates?: any
}

export interface BlockTypeRegisteredData {
  type: string
}

export interface BlockCreatedData extends BlockEventData {}
export interface BlockUpdatedData extends BlockEventData {}
export interface BlockDeletedData {
  blockId: string
}

/**
 * Page Events
 */
export interface PageEventData {
  page: any
  pageId?: string
}

export interface PageCreatedData extends PageEventData {}
export interface PageUpdatedData extends PageEventData {}
export interface PageDeletedData {
  pageId: string
}

/**
 * Command Events
 */
export interface CommandEventData {
  name: string
  moduleName?: string
  params?: any
  result?: any
  error?: string | Error
}

export interface CommandRegisteredData extends CommandEventData {}
export interface CommandUnregisteredData extends CommandEventData {}
export interface CommandErrorData extends CommandEventData {
  error: string | Error
}
export interface CommandExecutingData extends CommandEventData {}
export interface CommandExecutedData extends CommandEventData {}

/**
 * Editor Events
 */
export interface EditorEventData {
  transaction?: any
  cursor?: any
  selection?: any
  state?: any
}

export interface EditorTransactionAppliedData extends EditorEventData {}
export interface EditorCursorMovedData {
  cursor: any
}
export interface EditorSelectionChangedData {
  selection: any
}
export interface EditorTransactionCommittedData {
  transaction: any
}
export interface EditorTransactionRolledBackData {
  transaction: any
}
export interface EditorUndoData {
  transaction: any
}
export interface EditorRedoData {
  transaction: any
}

/**
 * Data Events
 */
export interface DataEventData {
  format?: string
  action?: string
  data?: any
}

export interface DataImportedData extends DataEventData {
  format: string
}
export interface DataExportedData extends DataEventData {
  format?: string
  action: string
}
export interface DataSavedData {
  data: any
}
export interface DataLoadedData {
  data: any
}

/**
 * Module Events
 */
export interface ModuleEventData {
  moduleName: string
  module?: any
  error?: string | Error
}

export interface ModuleLoadedData extends ModuleEventData {
  module: any
}
export interface ModuleLoadErrorData extends ModuleEventData {
  error: string | Error
}
export interface ModuleUnloadedData extends ModuleEventData {}
export interface ModuleUnloadErrorData extends ModuleEventData {
  error: string | Error
}

/**
 * Surface Events
 */
export interface SurfaceEventData {
  moduleName: string
  surface?: any
}

export interface SurfaceRegisteredData extends SurfaceEventData {}
export interface SurfaceUnregisteredData extends SurfaceEventData {}
export interface SurfaceEnabledData extends SurfaceEventData {}
export interface SurfaceDisabledData extends SurfaceEventData {}

/**
 * Notion Events
 */
export interface NotionEventData {
  format?: string
  error?: Error
  data?: any
}

export interface NotionImportStartedData extends NotionEventData {
  format: string
}
export interface NotionImportCompletedData extends NotionEventData {
  format: string
  data?: any
}
export interface NotionImportErrorData extends NotionEventData {
  error: Error
}
export interface NotionImportCancelledData extends NotionEventData {}
export interface NotionDataStoredData extends NotionEventData {
  data: any
}

/**
 * Navigation Events
 */
export interface NavigationEventData {
  route?: string
  page?: any
  params?: any
}

export interface NavigatedData extends NavigationEventData {}
export interface AppNavigateData extends NavigationEventData {}

/**
 * System Events
 */
export interface SystemEventData {
  type: string
  data?: any
}

export interface AutoSaveData {
  timestamp: Date
  data?: any
}

/**
 * All event types organized by category
 */
export const Events = {
  // Block Events
  BLOCK_TYPE_REGISTERED: `${EventCategories.BLOCK}:type:registered`,
  BLOCK_CREATED: `${EventCategories.BLOCK}:created`,
  BLOCK_UPDATED: `${EventCategories.BLOCK}:updated`,
  BLOCK_DELETED: `${EventCategories.BLOCK}:deleted`,
  
  // Page Events
  PAGE_CREATED: `${EventCategories.PAGE}:created`,
  PAGE_UPDATED: `${EventCategories.PAGE}:updated`,
  PAGE_DELETED: `${EventCategories.PAGE}:deleted`,
  
  // Command Events
  COMMAND_REGISTERED: `${EventCategories.COMMAND}:registered`,
  COMMAND_UNREGISTERED: `${EventCategories.COMMAND}:unregistered`,
  COMMAND_ERROR: `${EventCategories.COMMAND}:error`,
  COMMAND_EXECUTING: `${EventCategories.COMMAND}:executing`,
  COMMAND_EXECUTED: `${EventCategories.COMMAND}:executed`,
  
  // Editor Events
  EDITOR_TRANSACTION_APPLIED: `${EventCategories.EDITOR}:transaction:applied`,
  EDITOR_CURSOR_MOVED: `${EventCategories.EDITOR}:cursor:moved`,
  EDITOR_SELECTION_CHANGED: `${EventCategories.EDITOR}:selection:changed`,
  EDITOR_TRANSACTION_COMMITTED: `${EventCategories.EDITOR}:transaction:committed`,
  EDITOR_TRANSACTION_ROLLED_BACK: `${EventCategories.EDITOR}:transaction:rolled_back`,
  EDITOR_UNDO: `${EventCategories.EDITOR}:undo`,
  EDITOR_REDO: `${EventCategories.EDITOR}:redo`,
  
  // Data Events
  DATA_IMPORTED: `${EventCategories.DATA}:imported`,
  DATA_EXPORTED: `${EventCategories.DATA}:exported`,
  DATA_SAVED: `${EventCategories.DATA}:saved`,
  DATA_LOADED: `${EventCategories.DATA}:loaded`,
  
  // Module Events
  MODULE_LOADED: `${EventCategories.MODULE}:loaded`,
  MODULE_LOAD_ERROR: `${EventCategories.MODULE}:load:error`,
  MODULE_UNLOADED: `${EventCategories.MODULE}:unloaded`,
  MODULE_UNLOAD_ERROR: `${EventCategories.MODULE}:unload:error`,
  
  // Surface Events
  SURFACE_REGISTERED: `${EventCategories.SURFACE}:registered`,
  SURFACE_UNREGISTERED: `${EventCategories.SURFACE}:unregistered`,
  SURFACE_ENABLED: `${EventCategories.SURFACE}:enabled`,
  SURFACE_DISABLED: `${EventCategories.SURFACE}:disabled`,
  
  // Notion Events
  NOTION_IMPORT_STARTED: `${EventCategories.NOTION}:import:started`,
  NOTION_IMPORT_COMPLETED: `${EventCategories.NOTION}:import:completed`,
  NOTION_IMPORT_ERROR: `${EventCategories.NOTION}:import:error`,
  NOTION_IMPORT_CANCELLED: `${EventCategories.NOTION}:import:cancelled`,
  NOTION_DATA_STORED: `${EventCategories.NOTION}:data:stored`,
  
  // Navigation Events
  NAVIGATED: `${EventCategories.NAVIGATION}:navigated`,
  APP_NAVIGATE: `${EventCategories.NAVIGATION}:app:navigate`,
  
  // System Events
  AUTO_SAVE: `${EventCategories.SYSTEM}:auto_save`
} as const

/**
 * Event payload type mappings
 */
export type EventPayloads = {
  [Events.BLOCK_TYPE_REGISTERED]: BlockTypeRegisteredData
  [Events.BLOCK_CREATED]: BlockCreatedData
  [Events.BLOCK_UPDATED]: BlockUpdatedData
  [Events.BLOCK_DELETED]: BlockDeletedData
  [Events.PAGE_CREATED]: PageCreatedData
  [Events.PAGE_UPDATED]: PageUpdatedData
  [Events.PAGE_DELETED]: PageDeletedData
  [Events.COMMAND_REGISTERED]: CommandRegisteredData
  [Events.COMMAND_UNREGISTERED]: CommandUnregisteredData
  [Events.COMMAND_ERROR]: CommandErrorData
  [Events.COMMAND_EXECUTING]: CommandExecutingData
  [Events.COMMAND_EXECUTED]: CommandExecutedData
  [Events.EDITOR_TRANSACTION_APPLIED]: EditorTransactionAppliedData
  [Events.EDITOR_CURSOR_MOVED]: EditorCursorMovedData
  [Events.EDITOR_SELECTION_CHANGED]: EditorSelectionChangedData
  [Events.EDITOR_TRANSACTION_COMMITTED]: EditorTransactionCommittedData
  [Events.EDITOR_TRANSACTION_ROLLED_BACK]: EditorTransactionRolledBackData
  [Events.EDITOR_UNDO]: EditorUndoData
  [Events.EDITOR_REDO]: EditorRedoData
  [Events.DATA_IMPORTED]: DataImportedData
  [Events.DATA_EXPORTED]: DataExportedData
  [Events.DATA_SAVED]: DataSavedData
  [Events.DATA_LOADED]: DataLoadedData
  [Events.MODULE_LOADED]: ModuleLoadedData
  [Events.MODULE_LOAD_ERROR]: ModuleLoadErrorData
  [Events.MODULE_UNLOADED]: ModuleUnloadedData
  [Events.MODULE_UNLOAD_ERROR]: ModuleUnloadErrorData
  [Events.SURFACE_REGISTERED]: SurfaceRegisteredData
  [Events.SURFACE_UNREGISTERED]: SurfaceUnregisteredData
  [Events.SURFACE_ENABLED]: SurfaceEnabledData
  [Events.SURFACE_DISABLED]: SurfaceDisabledData
  [Events.NOTION_IMPORT_STARTED]: NotionImportStartedData
  [Events.NOTION_IMPORT_COMPLETED]: NotionImportCompletedData
  [Events.NOTION_IMPORT_ERROR]: NotionImportErrorData
  [Events.NOTION_IMPORT_CANCELLED]: NotionImportCancelledData
  [Events.NOTION_DATA_STORED]: NotionDataStoredData
  [Events.NAVIGATED]: NavigatedData
  [Events.APP_NAVIGATE]: AppNavigateData
  [Events.AUTO_SAVE]: AutoSaveData
}